import L from "leaflet";

// Shared cluster-bubble icon for the Leaflet maps that cluster thousands of markers.
//
// A count alone throws away the thing the map is actually about: on the SKU maps colour
// is the answer (carried / in stock / never), and on the stores map with several platform
// layers on it is which platform. So a collapsed cluster draws its children's mix as a
// conic-gradient donut ring with the count in the hole -- "mostly grey" or "mostly
// Blinkit" reads without expanding anything.
//
// Slice colours are always passed in by the caller from the backend contract (the
// platform registry's brand colour, a status palette); nothing here invents a colour.

export type Slice = { color: string; label: string; n: number };

/** Bubble diameter for a child count -- the same ladder on every clustered map. */
export function clusterSize(n: number): number {
  return n < 10 ? 26 : n < 100 ? 31 : n < 1000 ? 36 : 41;
}

/** 1234 -> "1.2k" so a big cluster's count still fits its bubble. */
export function clusterLabel(n: number): string {
  return n < 1000 ? String(n) : `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}k`;
}

/**
 * A donut cluster bubble: `slices` in the caller's order around the ring, `count` in the
 * hole, and the composition spelled out in the tooltip. Zero-count slices are dropped.
 */
export function donutClusterIcon(slices: Slice[], count: number, fallback: string): L.DivIcon {
  const present = slices.filter((s) => s.n > 0);
  const total = present.reduce((a, s) => a + s.n, 0) || 1;

  const stops: string[] = [];
  const parts: string[] = [];
  let acc = 0;
  for (const s of present) {
    const from = (acc / total) * 360;
    acc += s.n;
    const to = (acc / total) * 360;
    stops.push(`${s.color} ${from.toFixed(2)}deg ${to.toFixed(2)}deg`);
    parts.push(`${s.n} ${s.label}`);
  }

  const size = clusterSize(count);
  const el = document.createElement("div");
  el.className = "qcs-cl";
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.background = stops.length ? `conic-gradient(${stops.join(",")})` : fallback;
  el.title = `${count} stores: ${parts.join(", ")}`;
  const inner = document.createElement("span");
  inner.className = "qcs-cl__n";
  inner.textContent = clusterLabel(count);
  el.appendChild(inner);

  return L.divIcon({
    className: "",
    iconSize: L.point(size, size),
    iconAnchor: L.point(size / 2, size / 2),
    html: el.outerHTML,
  });
}
